/**
 * Who is on this thing, and how to change it — one panel, used by pathways AND courses.
 *
 * ONE COMPONENT RATHER THAN TWO, because the administrator's task is identical on both
 * surfaces: see the list, add somebody, take somebody off. The two differ in three details
 * that are passed in rather than branched on inside, so neither surface can quietly grow a
 * different answer to the same question:
 *
 *   `subject`     the noun in every sentence, so a refusal names the course or the pathway
 *   `inviteNote`  what an invitation ACTUALLY does here, which is genuinely different —
 *                 see the two call sites. A course invitation can send the platform's own
 *                 email; a pathway invitation cannot send anything at all.
 *   `showTrack`   courses carry an enrolment track, pathways do not
 *
 * WHAT THIS COMPONENT DOES NOT DO: decide authority. `writable` only chooses whether the
 * controls are drawn, and it comes from the server's own `writable_orgs`. Every button here
 * hits an endpoint that re-checks the tenant boundary, and a refusal is shown verbatim
 * rather than pre-empted — there is no client-side guard in front of a server rule, because
 * a guard that disagrees with the server is a lie in whichever direction it errs.
 */

import { useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Badge,
  Banner,
  Button,
  DataTable,
  Icon,
  Kpi,
  KpiRow,
  Pill,
  Spinner,
  type Column,
} from '../../shared/ui';
import {
  pathwayProblem,
  rosterAfterWrite,
  type Member,
  type MemberList,
  type RosterWrite,
} from '../../shared/lib/pathways-admin';
import './enrolments.css';

export interface RosterApi {
  /** React Query key for this roster. */
  queryKey: unknown[];
  list: () => Promise<MemberList>;
  /**
   * One person, by username OR email — never both. The server resolves which.
   *
   * Returns the roster the server rebuilt while serving the write, so the panel does not
   * have to ask for it again. `roster: null` means it did not send one — see `RosterWrite`.
   */
  add: (who: { username?: string; email?: string }, notify: boolean) => Promise<RosterWrite>;
  addMany: (emails: string[], notify: boolean) => Promise<unknown>;
  /** `identifier` is a username, or an email when there is no account yet. */
  remove: (identifier: string) => Promise<unknown>;
}

export function LearnerRoster({
  api,
  writable,
  subject,
  inviteNote,
  showTrack = false,
  notify,
  rowAction,
}: {
  api: RosterApi;
  writable: boolean;
  subject: 'learning path' | 'course';
  /** The honest explanation of what adding an unregistered email does on this surface. */
  inviteNote: ReactNode;
  showTrack?: boolean;
  /**
   * An extra per-row control, for a surface that has one the other does not.
   *
   * Passed in rather than branched on inside, exactly like `inviteNote` and `showTrack`, and
   * OMITTED ENTIRELY where a surface has no such control — so the column does not exist rather
   * than existing empty. The pathway surface passes the audited free-enrolment cancellation,
   * which has no course equivalent: a course enrolment reached through a purchase belongs on
   * the order screen, and one granted by a pathway is cancelled by cancelling the pathway.
   *
   * Returning `null` for a particular row is how a caller says "not this one" — a pending
   * invite has no enrolment to cancel.
   */
  rowAction?: (row: Member) => ReactNode;
  /**
   * The "email them" control, where the surface HAS one.
   *
   * Omitted entirely for pathways rather than shown-and-disabled: a checkbox that cannot
   * do anything is worse than no checkbox, because it implies the capability exists.
   */
  notify?: { label: string; hint: string };
}) {
  const queryClient = useQueryClient();
  const [who, setWho] = useState('');
  const [bulk, setBulk] = useState('');
  const [notifyOn, setNotifyOn] = useState(false);
  const [done, setDone] = useState<string | null>(null);

  const roster = useQuery({ queryKey: api.queryKey, queryFn: api.list, retry: false });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: api.queryKey });

  /**
   * Take the roster the write already returned, instead of asking for it again.
   *
   * Every write here changed the list and the server rebuilt the list to answer — so
   * `invalidate()` threw that answer away and issued a GET that rebuilt the identical list a
   * second time: two full builds for one write, and a visible gap between them in which the
   * panel showed the state before the change.
   *
   * `cancelQueries` FIRST, AND IT IS NOT DEFENSIVE — it replaces a guarantee that
   * `invalidateQueries` was providing silently and `setQueryData` does not.
   * `invalidateQueries` refetches with `cancelRefetch: true`, so an already-running GET is
   * ABORTED and superseded; the stale answer can never land. `setQueryData` cancels nothing:
   * an in-flight GET resolves afterwards and overwrites the patch with a body the server
   * computed BEFORE this write committed. Not a background-polling hypothetical either — the
   * two mutations below still call `invalidate()`, so "remove someone, then immediately add
   * someone", which is an ordinary correction, is enough to race it. And because
   * `setQueryData` marks the query fresh under the client's 30s `staleTime`, the wrong list
   * would then sit there for thirty seconds rather than being corrected on the next mount.
   *
   * Returns false when there is nothing usable to write in, and the caller invalidates.
   * Three ways that happens, all real:
   *
   *   `members` is null   the endpoint sent no roster (see `RosterWrite`), or an older image
   *                       is deployed. NOT every endpoint sends one — see the mutations.
   *   no `previous`       the list query has not answered yet, so there is no wrapper to
   *                       recompute `count`/`enrolled`/`invited` against. Rare (the panel
   *                       renders the form only once the list has loaded) and cheap to
   *                       handle by simply refetching.
   *   two writes at once  two `addOne` mutations in flight would let the last RESPONSE win
   *                       rather than the last WRITE. Prevented at the controls rather than
   *                       here — both the button and the Enter key refuse while one is
   *                       pending — because the fix belongs where the second write starts.
   */
  const applyRoster = async (members: Member[] | null): Promise<boolean> => {
    // Before reading `previous`, so a GET that is already running cannot resolve between the
    // read and the write and take the patch back with it.
    await queryClient.cancelQueries({ queryKey: api.queryKey });
    const previous = queryClient.getQueryData<MemberList>(api.queryKey);
    if (!members || !previous) return false;
    queryClient.setQueryData<MemberList>(api.queryKey, rosterAfterWrite(previous, members));
    return true;
  };

  /**
   * ONE FIELD for "who", and the '@' decides.
   *
   * Two fields would ask the administrator to classify what they already typed, and the
   * classification is unambiguous: an address contains '@' and a username cannot. The
   * server is still the one that resolves it — an unknown username is a 404 naming it, an
   * unknown address becomes an invitation — so this only chooses which key to send.
   */
  const addOne = useMutation({
    mutationFn: (value: string) =>
      api.add(value.includes('@') ? { email: value } : { username: value }, notifyOn),
    onSuccess: async (result, value) => {
      setDone(`${value} was added.`);
      setWho('');
      // The ONE mutation on this panel whose response carries the rebuilt roster, on both
      // surfaces. POST-then-GET becomes POST. MEASURED on a dev academy, 2026-08-18: the
      // course roster went POST 2,003 B + GET 1,751 B to POST alone, the
      // pathway roster POST 646 B + GET 531 B to POST alone, and both panels showed the
      // same counts afterwards as they did with the GET.
      if (!(await applyRoster(result.roster))) invalidate();
    },
  });

  const emails = bulk
    .split(/[\s,;]+/)
    .map((entry) => entry.trim())
    .filter(Boolean);

  /*
   * STILL INVALIDATES, and that is a statement about the API rather than an oversight.
   *
   * Checked endpoint by endpoint against the views, not assumed from the single-add case:
   * the bulk add (`POST .../learners/bulk`) and BOTH removals (`DELETE .../learners/{id}`,
   * `DELETE .../learners/bulk`) answer with what happened to each address — upstream's
   * `{before, after}` outcome rows — and no member list at all. There is nothing in those
   * bodies to write into the cache.
   *
   * Reconstructing the list here from the outcome rows is the thing NOT to do. A removal is
   * not "drop the row": an enrolled member becomes a `removed` row that stays in the list as
   * history, a cancelled PATHWAY invitation becomes `invite_withdrawn`, and a cancelled
   * COURSE invitation is deleted outright and leaves nothing. That is three server rules,
   * and a client that re-implements them is a fourth place for them to be wrong.
   *
   * So these two keep costing a GET. Removing it needs the endpoints to return the roster
   * the way the single add already does, which is a backend change and not this one.
   */
  const addBulk = useMutation({
    mutationFn: () => api.addMany(emails, notifyOn),
    onSuccess: () => {
      setDone(`${emails.length} ${emails.length === 1 ? 'person' : 'people'} added.`);
      setBulk('');
      invalidate();
    },
  });

  const drop = useMutation({
    mutationFn: (identifier: string) => api.remove(identifier),
    onSuccess: (_data, identifier) => {
      setDone(`${identifier} was removed.`);
      invalidate();
    },
  });

  const members = roster.data?.results ?? [];

  const columns: Column<Member>[] = [
    {
      key: 'person',
      header: 'Person',
      render: (row) =>
        row.username ? (
          <>
            {row.username}
            <small className="admin-sub">{row.email}</small>
          </>
        ) : (
          <>
            {row.email}
            {/* NAMED, not implied. There is no account behind this address yet, and an
                administrator who reads it as a normal member will wonder why the person
                cannot sign in. */}
            <small className="admin-sub">no account yet</small>
          </>
        ),
      mono: true,
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) =>
        row.status === 'enrolled' ? (
          <Pill tone="active">Enrolled</Pill>
        ) : row.status === 'invited' ? (
          <Pill tone="planned">Pending invite</Pill>
        ) : row.status === 'invite_withdrawn' ? (
          <Pill tone="retired">Invite cancelled</Pill>
        ) : (
          <Pill tone="retired">Removed</Pill>
        ),
    },
    ...(showTrack
      ? [
          {
            key: 'mode',
            header: 'Track',
            render: (row: Member) =>
              // Audit is the one track that cannot carry a certificate, so it is marked
              // rather than merely printed.
              row.mode ? (
                <Pill tone={row.mode === 'audit' ? 'suspended' : 'neutral'}>{row.mode}</Pill>
              ) : (
                <span className="admin-sub">—</span>
              ),
          } as Column<Member>,
        ]
      : []),
    {
      key: 'since',
      header: 'Since',
      render: (row) =>
        row.since
          ? new Date(row.since).toLocaleDateString(undefined, {
              day: 'numeric',
              month: 'short',
              year: 'numeric',
            })
          : '—',
    },
    ...(writable
      ? [
          {
            key: 'remove',
            header: '',
            render: (row: Member) =>
              row.is_active ? (
                <Button
                  variant="ghost"
                  busy={drop.isPending && drop.variables === (row.username ?? row.email)}
                  onClick={() => drop.mutate(row.username ?? row.email)}
                >
                  <Icon name="person_remove" />
                  {row.status === 'invited' ? 'Cancel invite' : 'Remove'}
                </Button>
              ) : null,
          } as Column<Member>,
        ]
      : []),
    // AFTER `remove`, and only when the surface passes one. Its own column rather than a second
    // control inside the remove cell, because the two are not variants of one action: see the
    // prop's own note.
    ...(writable && rowAction
      ? [
          {
            key: 'row-action',
            header: '',
            render: (row: Member) => rowAction(row),
          } as Column<Member>,
        ]
      : []),
  ];

  return (
    <section className="admin-panel">
      <div className="admin-panel__head">
        <h2>Learners</h2>
        {roster.data && (
          <Badge tone={roster.data.invited > 0 ? 'warning' : 'neutral'}>
            {roster.data.enrolled} enrolled
            {roster.data.invited > 0 ? ` · ${roster.data.invited} pending` : ''}
          </Badge>
        )}
      </div>

      {roster.isPending ? (
        <Spinner label="Loading who is on this…" />
      ) : roster.isError ? (
        <Problem error={roster.error} subject={subject} />
      ) : (
        <>
          <KpiRow>
            <Kpi label="Enrolled" value={roster.data?.enrolled ?? 0} />
            <Kpi
              label="Pending invites"
              value={roster.data?.invited ?? 0}
              hint={roster.data?.invited ? 'waiting for them to register' : undefined}
            />
          </KpiRow>

          <DataTable
            columns={columns}
            rows={members}
            rowKey={(row) => `${row.status}-${row.username ?? row.email}`}
            empty={`Nobody is on this ${subject} yet.`}
          />
        </>
      )}

      {/* THE INVITE RULE, shown whenever the controls are — not folded into a tooltip and
          not revealed only after somebody has already been invited. It changes what the
          administrator has to do next, so it belongs next to the control that triggers it.

          Both it and the form are withheld when the LIST ITSELF was refused. That is not a
          guard masking a server refusal — the refusal is displayed above, verbatim, and is
          the loudest thing on the panel. It is declining to offer an "Add" that the server
          has just finished explaining it will not accept, which would only produce the same
          sentence a second time. */}
      {!roster.isError && inviteNote}

      {writable && !roster.isError && (
        <>
          <div className="admin-form-grid">
            <label className="admin-field">
              <span>Add one person</span>
              <input
                className="enrol-input"
                value={who}
                onChange={(event) => setWho(event.target.value)}
                placeholder="username or someone@example.com"
                onKeyDown={(event) => {
                  // `!addOne.isPending` matches the Add button's own `disabled`, which this
                  // handler was silently bypassing: holding Enter fired a second write while
                  // the first was still in flight, and then the last RESPONSE decided what
                  // the roster looked like rather than the last write.
                  if (event.key === 'Enter' && who.trim() && !addOne.isPending) {
                    event.preventDefault();
                    addOne.mutate(who.trim());
                  }
                }}
              />
              <small>A username must already exist. An email address need not.</small>
            </label>

            <label className="admin-field">
              <span>Or add many, by email</span>
              <textarea
                rows={3}
                value={bulk}
                onChange={(event) => setBulk(event.target.value)}
                placeholder={'someone@example.com\nsomeone-else@example.com'}
              />
              <small>
                One per line, or separated by commas. If any address is malformed the whole
                list is refused, so nobody is silently left out.
              </small>
            </label>
          </div>

          {notify && (
            <div className="admin-checks">
              <label>
                <input
                  type="checkbox"
                  checked={notifyOn}
                  onChange={(event) => setNotifyOn(event.target.checked)}
                />
                <span>
                  {notify.label}
                  <small>{notify.hint}</small>
                </span>
              </label>
            </div>
          )}

          <div className="admin-actions">
            <Button
              variant="primary"
              busy={addOne.isPending}
              disabled={!who.trim() || addOne.isPending}
              onClick={() => addOne.mutate(who.trim())}
            >
              <Icon name="person_add" />
              Add
            </Button>
            <Button
              busy={addBulk.isPending}
              disabled={emails.length === 0 || addBulk.isPending}
              onClick={() => addBulk.mutate()}
            >
              {emails.length > 1 ? `Add ${emails.length} people` : 'Add list'}
            </Button>
          </div>

          {/* Each mutation reports its OWN failure, because "add" and "remove" fail for
              different reasons and a shared banner would attribute one to the other. */}
          <Problem error={addOne.error} subject={subject} when={addOne.isError} />
          <Problem error={addBulk.error} subject={subject} when={addBulk.isError} />
          <Problem error={drop.error} subject="learner" when={drop.isError} />

          {done && !addOne.isError && !addBulk.isError && !drop.isError && (
            <Banner tone="success">
              <strong>{done}</strong> The list above has been refreshed.
            </Banner>
          )}
        </>
      )}
    </section>
  );
}

/**
 * The server's own sentence, verbatim.
 *
 * Every refusal this API makes already names the thing — "No such course for this academy:
 * org <ORG>.", "someone@example.com is not enrolled in or invited to course-v1:…" —
 * so replacing it with "Something went wrong" would throw away the only part worth reading.
 */
function Problem({
  error,
  subject,
  when = true,
}: {
  error: unknown;
  subject: 'learning path' | 'course' | 'learner';
  when?: boolean;
}) {
  if (!when || !error) return null;
  const problem = pathwayProblem(error, subject);
  return (
    <Banner tone="danger">
      <strong>{problem.headline}</strong> {problem.detail}
    </Banner>
  );
}
